import React, { useEffect, useMemo, useState } from "react"
import { Spinner } from "reactstrap"
import DeliveryPartnerPermissionsEditor from "./DeliveryPartnerPermissionsEditor"
import { isDeliveryPartnerCategory } from "utils/deliveryPartnerPermissionLayout"
import "./role-permissions.scss"

const INTEGRATIONS_NAV_KEY = "integrations"

const getGroupTitle = (group, language) =>
  language === "sa" ? group.titleAr : group.title

const getPermissionLabel = (permission, language) =>
  language === "sa" ? permission.labelAr : permission.label

const countGroupPermissions = group => {
  let enabled = 0
  let total = 0
  group.permissions?.forEach(permission => {
    const subs = permission.subPermissions || []
    if (subs.length > 0) {
      subs.forEach(sub => {
        total += 1
        if (sub.isEnabled) enabled += 1
      })
    } else {
      total += 1
      if (permission.isEnabled) enabled += 1
    }
  })
  return { enabled, total }
}

const countIntegrationPermissions = (integrationPermissions, intPermDefs) => {
  let enabled = 0
  let total = 0
  Object.values(integrationPermissions || {}).forEach(perms => {
    intPermDefs.forEach(p => {
      total += 1
      if (perms?.[p.id]) enabled += 1
    })
  })
  return { enabled, total }
}

export const PermissionTile = ({ label, checked, onChange, isSub = false }) => {
  const handleActivate = e => {
    e.preventDefault()
    onChange?.()
  }

  return (
  <label
    className={`role-permissions-split__perm-tile${
      isSub ? " role-permissions-split__perm-tile--sub" : ""
    }${checked ? " role-permissions-split__perm-tile--checked" : ""}`}
    onClick={handleActivate}
    onKeyDown={e => {
      if (e.key === " " || e.key === "Enter") handleActivate(e)
    }}
    tabIndex={0}
    role="checkbox"
    aria-checked={checked}
  >
    <input
      type="checkbox"
      className="form-check-input role-permissions-split__perm-tile-input"
      checked={checked}
      readOnly
      tabIndex={-1}
      aria-hidden
    />
    <span className="role-permissions-split__perm-tile-label">{label}</span>
  </label>
  )
}

const SelectAllTile = ({ id, label, checked, onChange }) => (
  <label
    htmlFor={id}
    className="role-permissions-split__select-all-tile mb-0"
    onClick={e => {
      e.preventDefault()
      onChange?.()
    }}
  >
    <input
      className="form-check-input"
      id={id}
      type="checkbox"
      checked={checked}
      readOnly
      tabIndex={-1}
    />
    <span className="form-check-label small mb-0">{label}</span>
  </label>
)

const DetailPanel = ({ panelKey, children }) => (
  <div
    key={panelKey}
    className="role-permissions-split__detail-panel h-100 d-flex flex-column"
  >
    {children}
  </div>
)

const RolePermissionsSelector = ({
  t,
  language,
  permissionGroups = [],
  showIntegrations = false,
  integrationsTitle,
  catalogTree = [],
  catalogTreeLoading = false,
  fallbackIntegrations = [],
  integrationPermissions = {},
  intPermDefs = [],
  openIntegrationIds = [],
  onToggleIntegrationOpen,
  onPermissionChange,
  onCategorySelectAll,
  onIntegrationPermChange,
  onIntegrationSelectAll,
}) => {
  const navItems = useMemo(() => {
    const items = permissionGroups.map(group => ({
      key: String(group.id),
      type: "standard",
      group,
    }))
    if (showIntegrations) {
      items.push({ key: INTEGRATIONS_NAV_KEY, type: "integrations" })
    }
    return items
  }, [permissionGroups, showIntegrations])

  const [activeKey, setActiveKey] = useState(null)

  useEffect(() => {
    if (navItems.length === 0) {
      setActiveKey(null)
      return
    }
    const keys = navItems.map(item => item.key)
    if (!activeKey || !keys.includes(activeKey)) {
      setActiveKey(keys[0])
    }
  }, [navItems, activeKey])

  const activeItem = navItems.find(item => item.key === activeKey)
  const activeStandardGroup =
    activeItem?.type === "standard" ? activeItem.group : null

  const handleCategorySelectAll = group => {
    if (!group) return
    const { enabled, total } = countGroupPermissions(group)
    onCategorySelectAll?.(group.id, enabled < total)
  }

  const getNavBadgeClass = (enabled, total) => {
    if (total === 0 || enabled === 0) return ""
    if (enabled === total) return " role-permissions-split__nav-badge--full"
    return " role-permissions-split__nav-badge--partial"
  }

  const renderSubPermissions = (permissionGroup, permission) => {
    if (!permission.subPermissions?.length) {
      return null
    }
    return (
      <ul className="role-permissions-split__perm-list mt-2 mb-0">
        {permission.subPermissions.map(subPermission => (
          <li key={subPermission.id} className="role-permissions-split__perm-sub-wrap">
            <PermissionTile
              label={getPermissionLabel(subPermission, language)}
              checked={subPermission.isEnabled}
              isSub
              onChange={() =>
                onPermissionChange(
                  permissionGroup.id,
                  permission.id,
                  subPermission.id
                )
              }
            />
          </li>
        ))}
      </ul>
    )
  }

  const renderStandardDetail = group => {
    const { enabled, total } = countGroupPermissions(group)
    const allSelected = total > 0 && enabled === total

    return (
      <DetailPanel panelKey={String(group.id)}>
        <div className="role-permissions-split__detail-header">
          <h5 className="role-permissions-split__detail-title">
            {getGroupTitle(group, language)}
          </h5>
          <SelectAllTile
            id={`cat-all-${group.id}`}
            checked={allSelected}
            label={`${allSelected ? t("Deselect") : t("Select")} ${t("All")}`}
            onChange={() => handleCategorySelectAll(group)}
          />
        </div>
        <div className="role-permissions-split__detail-body">
          {isDeliveryPartnerCategory(group.id) ? (
            <DeliveryPartnerPermissionsEditor
              permissions={group.permissions}
              language={language}
              t={t}
              getPermissionLabel={getPermissionLabel}
              onPermissionChange={(permissionId, subPermissionId) =>
                onPermissionChange(group.id, permissionId, subPermissionId)
              }
            />
          ) : (
            <ul className="role-permissions-split__perm-list">
              {group.permissions.map(permission => (
                <li
                  key={permission.id}
                  className="role-permissions-split__perm-item-wrap"
                >
                  <PermissionTile
                    label={getPermissionLabel(permission, language)}
                    checked={permission.isEnabled}
                    onChange={() =>
                      onPermissionChange(group.id, permission.id)
                    }
                  />
                  {renderSubPermissions(group, permission)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DetailPanel>
    )
  }

  const flatIntegrations = useMemo(() => {
    const byId = new Map()
    catalogTree.forEach(category => {
      ;(category.integrations || []).forEach(integration => {
        const intKey = String(integration._id || integration.id || "")
        if (intKey) byId.set(intKey, integration)
      })
    })
    ;(fallbackIntegrations || []).forEach(integration => {
      const intKey = String(integration._id || integration.id || "")
      if (intKey && !byId.has(intKey)) byId.set(intKey, integration)
    })
    Object.keys(integrationPermissions || {}).forEach(intKey => {
      if (!byId.has(intKey)) {
        byId.set(intKey, {
          _id: intKey,
          code: intKey,
          title: { en: intKey, ar: intKey },
        })
      }
    })
    return Array.from(byId.values())
  }, [catalogTree, fallbackIntegrations, integrationPermissions])

  const renderIntegrationsDetail = () => {
    const { enabled, total } = countIntegrationPermissions(
      integrationPermissions,
      intPermDefs
    )

    return (
      <DetailPanel panelKey={INTEGRATIONS_NAV_KEY}>
        <div className="role-permissions-split__detail-header">
          <h5 className="role-permissions-split__detail-title">
            {integrationsTitle || t("Integrations")}
          </h5>
          {total > 0 && (
            <span className="small text-muted">
              {enabled}/{total} {t("selected")}
            </span>
          )}
        </div>
        <div className="role-permissions-split__detail-body">
          {catalogTreeLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted small py-4">
              <Spinner size="sm" color="primary" />
              {t("Loading integration catalog…")}
            </div>
          ) : flatIntegrations.length === 0 ? (
            <p className="text-muted small mb-0">
              {t("No integration groups in catalog.")}
            </p>
          ) : (
            flatIntegrations.map(integration => {
              const intKey = String(integration._id || integration.id || "")
              if (!intKey) return null
              const intName =
                (language === "sa"
                  ? integration.title?.ar
                  : integration.title?.en) ||
                integration.title?.en ||
                integration.code ||
                intKey
              const isOpen = openIntegrationIds.includes(intKey)
              const intPerms = integrationPermissions[intKey] || {}
              const allSelected =
                intPermDefs.length > 0 &&
                intPermDefs.every(p => !!intPerms[p.id])

              return (
                <div
                  key={intKey}
                  className={`role-permissions-split__integration-acc${
                    isOpen ? " role-permissions-split__integration-acc--open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="role-permissions-split__integration-toggle"
                    onClick={() => onToggleIntegrationOpen?.(intKey)}
                  >
                    <span className="font-size-13 fw-medium">{intName}</span>
                    <i
                      className={`bx ${
                        isOpen ? "bx-chevron-up" : "bx-chevron-right"
                      } text-muted`}
                      style={{ fontSize: "1.1rem" }}
                    />
                  </button>
                  {isOpen && (
                    <ul className="list-group list-group-flush border-top">
                      <li className="list-group-item bg-light p-2">
                        <SelectAllTile
                          id={`intperm-all-${intKey}`}
                          checked={allSelected}
                          label={`${
                            allSelected ? t("Deselect") : t("Select")
                          } ${t("All Permissions")}`}
                          onChange={() =>
                            onIntegrationSelectAll?.(intKey, !allSelected)
                          }
                        />
                      </li>
                      {intPermDefs.map(permission => (
                        <li className="list-group-item p-2" key={permission.id}>
                          <PermissionTile
                            label={getPermissionLabel(permission, language)}
                            checked={!!intPerms[permission.id]}
                            onChange={() =>
                              onIntegrationPermChange?.(intKey, permission.id)
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })
          )}
        </div>
      </DetailPanel>
    )
  }

  return (
    <div className="role-permissions-split">
      <nav className="role-permissions-split__nav" aria-label={t("Permissions")}>
        <div className="role-permissions-split__nav-header">
          {t("Permission categories")}
        </div>
        <ul className="role-permissions-split__nav-list">
          {navItems.map(item => {
            const isActive = item.key === activeKey
            let title
            let badge = null

            if (item.type === "standard") {
              title = getGroupTitle(item.group, language)
              const counts = countGroupPermissions(item.group)
              badge = (
                <span
                  className={`role-permissions-split__nav-badge${getNavBadgeClass(
                    counts.enabled,
                    counts.total
                  )}`}
                >
                  {counts.enabled}/{counts.total}
                </span>
              )
            } else {
              title = integrationsTitle || t("Integrations")
              const counts = countIntegrationPermissions(
                integrationPermissions,
                intPermDefs
              )
              badge = (
                <span
                  className={`role-permissions-split__nav-badge${getNavBadgeClass(
                    counts.enabled,
                    counts.total
                  )}`}
                >
                  {counts.enabled}/{counts.total}
                </span>
              )
            }

            return (
              <li key={item.key}>
                <button
                  type="button"
                  className={`role-permissions-split__nav-item${
                    isActive ? " role-permissions-split__nav-item--active" : ""
                  }`}
                  onClick={() => setActiveKey(item.key)}
                  aria-current={isActive ? "true" : undefined}
                >
                  <span className="role-permissions-split__nav-label-wrap">
                    <span className="role-permissions-split__nav-label">
                      {title}
                    </span>
                    {badge}
                  </span>
                  <i
                    className="bx bx-chevron-right role-permissions-split__nav-arrow"
                    aria-hidden
                  />
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="role-permissions-split__detail" key={activeKey || "empty"}>
        {!activeItem ? (
          <div className="role-permissions-split__empty">
            {t("No permissions available")}
          </div>
        ) : activeItem.type === "integrations" ? (
          renderIntegrationsDetail()
        ) : activeStandardGroup ? (
          renderStandardDetail(activeStandardGroup)
        ) : null}
      </div>
    </div>
  )
}

export default RolePermissionsSelector
